import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

const ONBOARDING_KEY = ["onboarding-progress"];

export interface OnboardingCheckpoint {
  org_created_at: string | null;
  invite_sent_at: string | null;
  whatsapp_connected_at: string | null;
  agent_configured_at: string | null;
  first_command_at: string | null;
  first_metric_at: string | null;
}

const CHECKPOINT_FIELDS = [
  "org_created_at",
  "invite_sent_at",
  "whatsapp_connected_at",
  "agent_configured_at",
  "first_command_at",
  "first_metric_at",
] as const;

export type CheckpointKey = (typeof CHECKPOINT_FIELDS)[number];

export const ONBOARDING_STEPS = [
  { key: "org_created_at" as const, label: "Criar organização", event: "org.created" },
  { key: "invite_sent_at" as const, label: "Convidar equipe", event: "team.invited" },
  { key: "whatsapp_connected_at" as const, label: "Conectar WhatsApp", event: "whatsapp.connected" },
  { key: "agent_configured_at" as const, label: "Configurar agente", event: "agent.configured" },
  { key: "first_command_at" as const, label: "Primeiro comando", event: "first_command.sent" },
  { key: "first_metric_at" as const, label: "Primeira métrica", event: "first_metric.loaded" },
] as const;

export function useOnboardingProgress() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ONBOARDING_KEY,
    enabled: !!user,
    queryFn: async () => {
      if (!user) return null;
      // Get user's org
      const { data: membership } = await supabase
        .from("org_members")
        .select("org_id")
        .eq("user_id", user.id)
        .limit(1)
        .maybeSingle();

      if (!membership) return null;

      const { data, error } = await supabase
        .from("onboarding_progress")
        .select("*")
        .eq("org_id", membership.org_id)
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;
      return data ? { ...data, org_id: membership.org_id } : { org_id: membership.org_id };
    },
  });

  const markCheckpoint = useMutation({
    mutationFn: async (checkpoint: CheckpointKey) => {
      if (!user || !query.data?.org_id) throw new Error("No user or org");
      const now = new Date().toISOString();

      const row: {
        org_id: string;
        user_id: string;
        org_created_at?: string;
        invite_sent_at?: string;
        whatsapp_connected_at?: string;
        agent_configured_at?: string;
        first_command_at?: string;
        first_metric_at?: string;
      } = {
        org_id: query.data.org_id,
        user_id: user.id,
      };
      row[checkpoint] = now;

      const { error } = await supabase
        .from("onboarding_progress")
        .upsert(row, { onConflict: "org_id,user_id" });

      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ONBOARDING_KEY }),
  });

  const completedSteps = ONBOARDING_STEPS.filter(
    (s) => query.data && (query.data as Record<string, unknown>)[s.key]
  ).length;

  const totalSteps = ONBOARDING_STEPS.length;
  const progressPct = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

  const isComplete = completedSteps === totalSteps;

  const currentStepIndex = ONBOARDING_STEPS.findIndex(
    (s) => !query.data || !(query.data as Record<string, unknown>)[s.key]
  );

  return {
    progress: query.data,
    isLoading: query.isLoading,
    error: query.error,
    completedSteps,
    totalSteps,
    progressPct,
    isComplete,
    currentStepIndex: currentStepIndex === -1 ? totalSteps : currentStepIndex,
    markCheckpoint,
    refetch: query.refetch,
  };
}
